import http, {RequestOptions, noContent, unwrap} from "@/api/http";
import {
    FulfillPartnerOrderInput,
    Page,
    PartnerOrderDto,
    PartnerOrderStatus,
    ResendPartnerOrderInput
} from "@/api/types";

export interface PartnerOrderFilter {
    /** Order name including the `#`, e.g. `"#1234"`. Also makes the server compute `canFulfill`. */
    orderName?: string;
    status?: PartnerOrderStatus;
    hasLabel?: boolean;
    /** Only partner orders whose order was created at or after this instant. */
    from?: string | Date;
    /** Admins only; for everyone else the server uses their own partner. Send it only when the user has a partner. */
    partnerId?: number;
    /** `"lineItems"` adds `lineItems` to every row. */
    include?: "lineItems";
}

export interface PartnerOrderPaging {
    /** Zero-based. */
    page: number;
    /** At most 100. */
    size?: number;
}

/**
 * `GET /api/partner-orders` (#14, P; an admin may pass `partnerId`).
 *
 * Without `page` the answer is a plain array, with `page` (and optionally `size`) it is a `Page`, newest first.
 */
export function listPartnerOrders(
    params: PartnerOrderFilter & PartnerOrderPaging,
    options?: RequestOptions
): Promise<Page<PartnerOrderDto>>;
export function listPartnerOrders(
    params?: PartnerOrderFilter & { page?: undefined; size?: undefined },
    options?: RequestOptions
): Promise<PartnerOrderDto[]>;
export function listPartnerOrders(
    params: PartnerOrderFilter & { page?: number; size?: number } = {},
    options?: RequestOptions
): Promise<Page<PartnerOrderDto> | PartnerOrderDto[]> {
    return http.get<Page<PartnerOrderDto> | PartnerOrderDto[]>("/partner-orders", {...options, params}).then(unwrap);
}

/** `POST /api/partner-orders/{id}/resend` (#15, admin). 202; 404 if the id is unknown. */
export const resendPartnerOrder = (id: number, input: ResendPartnerOrderInput, options?: RequestOptions): Promise<void> =>
    http.post(`/partner-orders/${id}/resend`, input, options).then(noContent);

/**
 * `POST /api/partner-orders/{id}/fulfill` (#16, admin). Resolves with the updated partner order; a Shopify failure is
 * 502 `FULFILLMENT_FAILED` (local changes rolled back). The fulfil dialog shows its own text for that, so it calls
 * this with `{skipErrorToast: true}`.
 */
export const fulfillPartnerOrder = (id: number, input: FulfillPartnerOrderInput, options?: RequestOptions): Promise<PartnerOrderDto> =>
    http.post<PartnerOrderDto>(`/partner-orders/${id}/fulfill`, input, options).then(unwrap);
